// CarouselSettings — the authoring pane for the Image Carousel block (DL-041),
// wired as Carousel.craft.related.settings. Follows the deck's "Basic / Advanced
// ToolAccordion" layout using the app's SHIPPED controls, not bespoke ones:
//   BASIC · Images   — the shared multi-image <List> (drag-reorder / remove / edit
//                       / replace) + the shared <MediaSelection> picker (Upload /
//                       Embed / Browse / Recent), exactly as the ImageGrid block
//                       wires them (everything-is-a-shared-component).
//   BASIC · Playback — Mode (Manual | Auto) + Speed (Slow | Medium | Fast); Speed
//                       is disabled with an "Auto only" note while Manual, per deck.
//   ADVANCED         — Fit (Keep shape | Crop | Fill) + Show captions / arrows /
//                       dots toggles. Collapsed by default (<AdvancedOptions>).
// Reads/writes node props via useNode()/setProp; the image upsert-by-id contract
// mirrors ImageGridSettings so Upload's two-phase (placeholder -> resolved) add works.

import { useNode, useEditor } from "@craftjs/core";
import React, { useState } from "react";
import styled from "styled-components";
import { fontFamily, brand } from "../../../../theme/tokens";
import EditorSection from "../../BlockEditorPane/EditorSection";
import AdvancedOptions from "../../BlockEditorPane/AdvancedOptions";
import { SegChoice, Toggle, QuadStepper } from "../../BlockEditorPane/controls";
import List from "../ImageGrid/List";
import { isLastImageRemoval } from "../ImageGrid/listPanel";
import MediaSelection from "../../Shared/MediaSelection";
import { FIT_MODES } from "./carouselLogic";

const Pane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: ${fontFamily};
  box-sizing: border-box;
  & * {
    box-sizing: border-box;
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-family: ${fontFamily};
  font-size: 12px;
  font-style: italic;
  color: #667085;
  line-height: 1.4;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FieldName = styled.span`
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: #123051;
`;

const SubNote = styled.span`
  display: block;
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 500;
  color: #98a2b3;
  margin-top: 2px;
`;

// Greys out the Speed control while Manual (deck: "Auto only"). Keeps it visible
// but non-interactive + removed from the tab order, with aria-disabled for AT.
const MaybeDisabled = styled.div`
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
`;

const AddBtn = styled.button`
  align-self: flex-start;
  min-height: 40px;
  padding: 0 14px;
  border: 1px dashed ${brand.primary};
  border-radius: 8px;
  background: #f7f2ff;
  color: #7b3fd6;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #efe4fe;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand.focusRing};
  }
`;

const CarouselSettings = () => {
  const {
    id,
    actions: { setProp },
    images,
    shallowImages,
    mode,
    speed,
    fit,
    showCaptions,
    showArrows,
    showDots,
    margin,
  } = useNode((node) => ({
    images: node.data.props.images || [],
    shallowImages: node.data.props.shallowImages || [],
    mode: node.data.props.mode,
    speed: node.data.props.speed,
    fit: node.data.props.fit,
    showCaptions: node.data.props.showCaptions,
    showArrows: node.data.props.showArrows,
    showDots: node.data.props.showDots,
    margin: node.data.props.margin,
  }));

  const {
    actions: { delete: deleteNode },
  } = useEditor();

  const [showAdd, setShowAdd] = useState((images || []).length === 0);

  // Upsert-by-id image contract (parity with ImageGridSettings): the Upload flow
  // fires twice per file — first a {id, loading:true} placeholder, then the
  // resolved {id, src, title, loading:false} — so "images" must update-in-place.
  const handleChange = (type, value) => {
    // Gordon 12:25 PT spec part 2, same rule as ImageGridSettings: emptying
    // this block's sole content removes the whole block instead of leaving
    // its empty-state skeleton.
    if (
      type === "removeImage" &&
      isLastImageRemoval(images, (images || []).filter((im) => im.id !== value))
    ) {
      deleteNode(id);
      return;
    }
    if (type === "newImages" && isLastImageRemoval(images, value)) {
      deleteNode(id);
      return;
    }

    setProp((props) => {
      switch (type) {
        case "images": {
          if (!Array.isArray(props.images)) props.images = [];
          const idx = props.images.findIndex((im) => im.id === value.id);
          if (idx !== -1) {
            props.images[idx] = { ...props.images[idx], ...value };
          } else {
            props.images.push(value);
          }
          break;
        }
        case "removeImage":
          props.images = (props.images || []).filter((im) => im.id !== value);
          props.shallowImages = (props.shallowImages || []).filter(
            (im) => im.id !== value
          );
          break;
        case "newImages":
          props.images = value;
          break;
        case "shallowImages":
          props.shallowImages = value;
          break;
        case "mode":
          props.mode = value;
          break;
        case "speed":
          props.speed = value;
          break;
        case "fit":
          props.fit = value;
          break;
        case "showCaptions":
          props.showCaptions = value;
          break;
        case "showArrows":
          props.showArrows = value;
          break;
        case "showDots":
          props.showDots = value;
          break;
        default:
          break;
      }
    });
  };

  const handleMarginChange = (side, value) => {
    setProp((props) => {
      props.margin = { top: 0, right: 0, bottom: 0, left: 0, ...props.margin, [side]: value };
    });
  };

  const isAuto = mode === "auto";

  return (
    <Pane>
      <EditorSection heading="Images">
        <HelpText>
          Drag a photo to reorder · use the trash to remove · use + to add more.
        </HelpText>
        <List
          images={images}
          setImages={(imgs) => handleChange("newImages", imgs)}
          shallowImages={shallowImages}
          setShallowImages={(imgs) => handleChange("shallowImages", imgs)}
          onClickAdd={() => setShowAdd(true)}
          showCaptions={!!showCaptions}
          onAutoEnableCaptions={() => handleChange("showCaptions", true)}
          showAddButton={false}
        />
        {!showAdd && (
          <AddBtn type="button" onClick={() => setShowAdd(true)}>
            + Add images
          </AddBtn>
        )}
        {showAdd && (
          <MediaSelection
            type="images"
            isGifAllowed
            handleChange={handleChange}
            handleRemove={(id) => handleChange("removeImage", id)}
            media={images}
            setMedia={(imgs) => handleChange("newImages", imgs)}
          />
        )}
      </EditorSection>

      <EditorSection heading="Playback">
        <HelpText>
          Manual = the visitor flips through it. Auto = it advances on its own.
        </HelpText>
        <FieldRow>
          <FieldName>Mode</FieldName>
          <SegChoice
            value={mode || "manual"}
            options={[
              { label: "Manual", value: "manual" },
              { label: "Auto", value: "auto" },
            ]}
            onChange={(v) => handleChange("mode", v)}
          />
        </FieldRow>
        <FieldRow>
          <div>
            <FieldName>Speed</FieldName>
            {!isAuto && <SubNote>Auto only</SubNote>}
          </div>
          <MaybeDisabled $disabled={!isAuto} aria-disabled={!isAuto}>
            <SegChoice
              value={speed || "medium"}
              options={[
                { label: "Slow", value: "slow" },
                { label: "Medium", value: "medium" },
                { label: "Fast", value: "fast" },
              ]}
              onChange={(v) => handleChange("speed", v)}
            />
          </MaybeDisabled>
        </FieldRow>
      </EditorSection>

      <AdvancedOptions>
        <FieldName as="div" style={{ marginBottom: 4 }}>
          Fit
        </FieldName>
        <HelpText style={{ marginBottom: 8 }}>
          Keep shape = each photo keeps its own outline at a shared height. Crop or
          Fill make every photo the same size.
        </HelpText>
        <SegChoice
          value={fit || FIT_MODES.keepShape}
          options={[
            { label: "Keep shape", value: FIT_MODES.keepShape },
            { label: "Crop", value: FIT_MODES.crop },
            { label: "Fill", value: FIT_MODES.fill },
          ]}
          onChange={(v) => handleChange("fit", v)}
        />
        <Toggle
          label="Show captions"
          checked={!!showCaptions}
          onChange={(v) => handleChange("showCaptions", v)}
        />
        {/* QA-46 (Kelly, 2026-07-31): "the toggle for Show Captions toggles off
            and on, but there is nowhere to write a caption". The input DOES
            exist � a slide's caption is its image name, edited with the pencil
            on that image in the Images list above � but nothing on this toggle
            said so, and the control is called "name", never "caption". Point at
            it from the toggle rather than adding a second, competing field. */}
        <SubNote style={{ marginTop: -8 }}>
          A caption is the image&rsquo;s name &mdash; set one with the pencil
          beside any image in Images above.
        </SubNote>
        <Toggle
          label="Show arrows"
          checked={showArrows !== false}
          onChange={(v) => handleChange("showArrows", v)}
        />
        <Toggle
          label="Show dots"
          checked={showDots !== false}
          onChange={(v) => handleChange("showDots", v)}
        />
        <QuadStepper
          label="Margin"
          value={margin || { top: 0, right: 0, bottom: 0, left: 0 }}
          onChange={handleMarginChange}
        />
      </AdvancedOptions>
    </Pane>
  );
};

export default CarouselSettings;
